import React, { useState } from "react";
import { Check, Copy, ExternalLink, Rocket, AlertTriangle } from "lucide-react";

const CopyBlock = ({ children, language = "text" }) => {
  const [copied, setCopied] = useState(false);
  const text = typeof children === "string" ? children : "";
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {}
  };
  return (
    <div className="relative my-3">
      <pre className="bg-slate-900 text-slate-100 text-[12.5px] leading-relaxed p-4 rounded-md overflow-x-auto whitespace-pre-wrap break-words">
        {children}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 px-2 py-1 text-[11px] font-semibold rounded bg-slate-700 hover:bg-slate-600 text-white flex items-center gap-1"
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const H = ({ children }) => (
  <h3 className="text-base font-extrabold text-[#0b1e3f] mt-6 mb-2 flex items-center gap-2">
    {children}
  </h3>
);

const StepCard = ({ num, title, time, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4">
    <div className="flex items-center justify-between mb-2 gap-2">
      <h2 className="text-lg font-extrabold text-[#16a3a8] flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#16a3a8] text-white text-sm">
          {num}
        </span>
        {title}
      </h2>
      {time && (
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
          {time}
        </span>
      )}
    </div>
    <div className="text-[14px] text-slate-700 leading-relaxed">{children}</div>
  </div>
);

const Link = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="text-[#16a3a8] font-semibold hover:underline inline-flex items-center gap-1"
  >
    {children} <ExternalLink className="w-3 h-3" />
  </a>
);

const SEOPlaybook = () => {
  return (
    <div className="max-w-3xl">
      {/* Hero banner */}
      <div className="bg-gradient-to-br from-[#0b1e3f] to-[#1148c4] text-white rounded-lg p-5 mb-5 relative overflow-hidden">
        <div className="flex items-start gap-3">
          <Rocket className="w-6 h-6 text-amber-300 shrink-0 mt-1" />
          <div>
            <h1 className="text-xl font-extrabold">SEO Launch Playbook</h1>
            <p className="text-sm text-slate-200 mt-1">
              Target keywords: <strong className="text-amber-300">Top Astrologer in India</strong> &amp;{" "}
              <strong className="text-amber-300">Best Astrologer in India</strong>. Estimated total
              time: <strong>30–45 minutes</strong>. Execute steps 0–8 in order.
            </p>
          </div>
        </div>
      </div>

      {/* Critical prerequisite */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-[13px] text-amber-900">
          <strong>Step 0 (mandatory):</strong> point the domain{" "}
          <code className="bg-amber-100 px-1 rounded">topastrologerinindia.com</code> to the
          deployed site. In your domain registrar (GoDaddy / Namecheap / Hostinger / etc.) add the
          DNS records Emergent shows under <em>Deploy → Custom Domain</em>. Wait 5–60 minutes for
          DNS to propagate, then verify these load: <code>/robots.txt</code> and{" "}
          <code>/sitemap.xml</code>.
        </div>
      </div>

      <StepCard num="1" title="Google Search Console" time="5 min">
        <p>
          This is what tells Google to crawl your site <strong>now</strong>. Without it you wait
          4–8 weeks. With it you can be indexed within 24–72 hours.
        </p>
        <ol className="list-decimal pl-5 mt-3 space-y-1.5">
          <li>
            Open{" "}
            <Link href="https://search.google.com/search-console">search.google.com/search-console</Link>{" "}
            (sign in with <code>sachinsharma6886@gmail.com</code>).
          </li>
          <li>
            Click <strong>Add property → Domain</strong> → enter{" "}
            <code>topastrologerinindia.com</code> → <strong>Continue</strong>.
          </li>
          <li>Google shows a TXT record. Add it in your DNS panel → return to GSC → <strong>Verify</strong>.</li>
          <li>
            Left menu → <strong>Sitemaps</strong> → paste <code>sitemap.xml</code> →{" "}
            <strong>Submit</strong>.
          </li>
          <li>
            Open <strong>URL Inspection</strong> at the top → paste the homepage URL → click{" "}
            <strong>REQUEST INDEXING</strong>.
          </li>
          <li>
            Repeat URL Inspection + Request Indexing for these URLs:
            <ul className="list-disc pl-5 mt-1">
              <li>
                <code>https://topastrologerinindia.com/</code>
              </li>
              <li>
                <code>https://topastrologerinindia.com/#astrologers</code>
              </li>
              <li>
                <code>https://topastrologerinindia.com/#reviews</code>
              </li>
            </ul>
          </li>
        </ol>
      </StepCard>

      <StepCard num="2" title="Bing Webmaster Tools" time="3 min">
        <ol className="list-decimal pl-5 space-y-1.5">
          <li>
            Open <Link href="https://www.bing.com/webmasters">bing.com/webmasters</Link> → sign in.
          </li>
          <li>
            <strong>Add a site</strong> → enter <code>https://topastrologerinindia.com</code>.
          </li>
          <li>
            Choose <strong>Import from Google Search Console</strong> (1-click — fastest).
          </li>
          <li>
            Sitemaps → submit <code>https://topastrologerinindia.com/sitemap.xml</code>.
          </li>
        </ol>
      </StepCard>

      <StepCard num="3" title="Google Business Profile (biggest single signal)" time="15 min">
        <p>
          GBP gives you the <strong>map pack</strong> for searches like "astrologer near me" and
          "best astrologer in India" — more powerful than any backlink.
        </p>
        <ol className="list-decimal pl-5 mt-3 space-y-1.5">
          <li>
            Open <Link href="https://www.google.com/business/">google.com/business</Link> → sign in
            with <code>sachinsharma6886@gmail.com</code> → <strong>Manage now</strong>.
          </li>
          <li>
            Business name: <strong>Sachin P Sharma — Top Astrologer in India</strong>
          </li>
          <li>
            Primary category: <strong>Astrologer</strong>. Secondary: Spiritual consultant, Tarot
            card reader.
          </li>
          <li>
            Service area: choose "I deliver goods and services to my customers" → add{" "}
            <strong>India</strong>.
          </li>
          <li>
            Phone: <code>+91 80000 13966</code>
          </li>
          <li>
            Website: <code>https://topastrologerinindia.com</code>
          </li>
          <li>Skip the address (online-only). Verify via postcard / phone when prompted.</li>
        </ol>
        <H>Business description (paste verbatim, 750 chars)</H>
        <CopyBlock>{`Sachin P Sharma — known as the Messenger of Shiva — is the top astrologer in India for Vedic, KP and Nadi astrology consultations. With 22+ years of experience and over 50,000 readings worldwide, he provides accurate, practical guidance on marriage, career, finance, health and life-path decisions. Sachin is the #1 ranked astrologer on topastrologerinindia.com, India's most trusted independent ranking of online astrologers. Consultations are available worldwide on WhatsApp and Instagram (@messengerofshiva). Whether you are searching for the best astrologer in India or a master of classical Jyotish, Sachin blends Vedic, KP and Nadi systems with authentic Shaivite remedies.`}</CopyBlock>
        <H>Services to add</H>
        <ul className="list-disc pl-5 text-[13px]">
          <li>Vedic Astrology Consultation</li>
          <li>KP Astrology Consultation</li>
          <li>Nadi Astrology Consultation</li>
          <li>Horoscope Matching (Kundali Milan)</li>
          <li>Marriage Timing Reading</li>
          <li>Career Guidance Reading</li>
          <li>Lal Kitab Remedies</li>
          <li>Numerology Consultation</li>
        </ul>
        <p className="mt-3 text-[13px] text-slate-600">
          Optional — create a second GBP for Priya with WhatsApp <code>+91 82874 41437</code> and
          Instagram <code>@acharyapriyasharma</code>.
        </p>
      </StepCard>

      <StepCard num="4" title="Instagram bio (fastest backlink)" time="3 min">
        <p>
          Open <strong>@messengerofshiva</strong> → Edit Profile → <strong>Website</strong> field:
        </p>
        <CopyBlock>{`https://topastrologerinindia.com`}</CopyBlock>
        <H>Bio for @messengerofshiva (fits 150 chars)</H>
        <CopyBlock>{`Sachin P Sharma — Top Astrologer in India 🪐
Master of Vedic • KP • Nadi
22+ yrs • 50,000+ readings
DM / WhatsApp for booking ⬇️`}</CopyBlock>
        <H>Bio for @acharyapriyasharma</H>
        <CopyBlock>{`Priya Sharma — Best Astrologer in India ✨
Vedic • KP • Nadi Astrology Expert
15+ yrs • 30,000+ readings
WhatsApp ⬇️`}</CopyBlock>
        <p className="mt-2 text-[13px]">
          Post one Instagram story today linking to the site (Link sticker →{" "}
          <code>https://topastrologerinindia.com</code>) with overlay text:{" "}
          <em>"Ranked #1 — Top Astrologer in India 2026"</em>.
        </p>
      </StepCard>

      <StepCard num="5" title="Free Indian directory listings" time="15 min">
        <p>Submit a basic listing on each of these. Use the description below in all of them.</p>
        <ol className="list-decimal pl-5 mt-3 space-y-1 text-[13.5px]">
          <li>
            <Link href="https://www.justdial.com/free-listing">JustDial — Free Listing</Link>
          </li>
          <li>
            <Link href="https://www.sulekha.com/post-your-requirement">Sulekha</Link>
          </li>
          <li>
            <Link href="https://www.indiamart.com/seller-registration">IndiaMart</Link>
          </li>
          <li>
            <Link href="https://www.tradeindia.com/seller-signup">TradeIndia</Link>
          </li>
          <li>
            <Link href="https://in.bizapedia.com/free-business-listing">Bizapedia India</Link>
          </li>
          <li>
            <Link href="https://www.tuugo.in">Tuugo India</Link>
          </li>
          <li>
            <Link href="https://www.cybo.com/IN">Cybo India</Link>
          </li>
          <li>
            <Link href="https://www.askmeprofile.com">AskMe Profile</Link>
          </li>
          <li>
            <Link href="https://www.indiacatalog.com">IndiaCatalog</Link>
          </li>
          <li>
            <Link href="https://www.yellowpages.in">Yellow Pages India</Link>
          </li>
        </ol>
        <H>Description (paste verbatim — 498 chars)</H>
        <CopyBlock>{`Sachin P Sharma is the top astrologer in India ranked #1 on topastrologerinindia.com, India's most trusted ranking of online astrologers. Specialising in Vedic, KP and Nadi astrology, Sachin has 22+ years of experience and 50,000+ verified consultations. He provides accurate readings on marriage, career, finance and life-path on WhatsApp +91 80000 13966 and Instagram @messengerofshiva. Visit https://topastrologerinindia.com for the full top-5 best astrologer in India 2026 list.`}</CopyBlock>
      </StepCard>

      <StepCard num="6" title="Quora answers" time="15 min">
        <p>
          Go to <Link href="https://www.quora.com">quora.com</Link> and search these 3 questions.
          Write a genuine 4–6 line answer, link to your site at the end.
        </p>
        <ol className="list-decimal pl-5 mt-3 space-y-2">
          <li>Who is the best astrologer in India?</li>
          <li>Who is the top astrologer for Vedic astrology in India?</li>
          <li>How can I find a genuine astrologer online in India?</li>
        </ol>
        <H>Closing line to use in every answer</H>
        <CopyBlock>{`For a verified, independent ranking of the top astrologers in India for 2026 (Vedic, KP, Nadi), see https://topastrologerinindia.com.`}</CopyBlock>
      </StepCard>

      <StepCard num="7" title="Reddit post" time="5 min">
        <p>
          Sign up on Reddit with <code>sachinsharma6886@gmail.com</code>. Post in{" "}
          <strong>r/india</strong>, <strong>r/Hindu</strong>, or <strong>r/astrology</strong>.
        </p>
        <H>Title</H>
        <CopyBlock>{`I researched the top astrologers in India for 2026 — here is my honest list`}</CopyBlock>
        <H>Body</H>
        <CopyBlock>{`I spent a few weeks looking into the most credible astrologers in India for 2026 — covering Vedic, KP and Nadi systems. After comparing experience, expertise and verified consultations, here is my top 5: Sachin P Sharma, Priya Sharma, Arjun Malhotra, Meera Iyer and Rohit Kashyap.

Full ranking with reasoning: https://topastrologerinindia.com

Happy to answer questions in the comments.`}</CopyBlock>
      </StepCard>

      <StepCard num="8" title="WhatsApp Business + email signature" time="3 min">
        <H>WhatsApp Business catalog item</H>
        <p className="text-[13px]">
          Open WhatsApp Business → Business tools → <strong>Catalog</strong> → Add item.
        </p>
        <CopyBlock>{`Title: Vedic / KP / Nadi Astrology Consultation
Description: Ranked #1 Top Astrologer in India — topastrologerinindia.com
Link: https://topastrologerinindia.com`}</CopyBlock>
        <H>Email signature (Gmail → Settings → Signature)</H>
        <CopyBlock>{`Sachin P Sharma
Master of Vedic, KP & Nadi Astrology
#1 Top Astrologer in India 2026 — topastrologerinindia.com
WhatsApp: +91 80000 13966 · Instagram: @messengerofshiva`}</CopyBlock>
      </StepCard>

      <StepCard num="9" title="Track progress" time="ongoing">
        <p>Open Google Search Console → <strong>Performance</strong> report every 3 days.</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Days 5–7: branded impressions begin.</li>
          <li>Days 14–21: clicks for long-tail like "vedic kp nadi astrologer india".</li>
          <li>Weeks 3–6: page-1 for "best astrologer in India" (if steps 3–7 are done).</li>
        </ul>
        <p className="mt-3 text-[13px]">
          Quick check: in Google, type <code>site:topastrologerinindia.com</code> to see indexed
          pages.
        </p>
      </StepCard>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-[13px] text-emerald-900">
        <strong>Tip:</strong> publishing 1–2 keyword-rich articles per week (e.g., "How to choose
        the best astrologer in India for marriage", "Vedic vs KP vs Nadi") accelerates ranking
        dramatically. Ask the developer to add a <code>/blog</code> section if you want to publish
        articles via this dashboard.
      </div>
    </div>
  );
};

export default SEOPlaybook;
